/**
 * Optional honesty fields on the fit result (families programme, contract with src/families):
 * `unmodelledFeatures` (FND-09, FND-13, FND-08), `fidelity` / `structureWarning` (FND-05, FND-06),
 * `evidence.frontQuad` / `viewYawDeg` (FND-16), and the photo's tone for the Check material
 * preview (FND-07). Every field is optional; the UI renders what is present and stays quiet
 * otherwise.
 */
import { computed } from 'vue';
import { isFitResult, type FitResult } from '@/families/fit-schema';
import { MATERIAL_CHOICES } from '@/families/stock';
import { ui } from './state';

type Pt = { x: number; y: number };
/** Below this fidelity score Check says the photo doesn't fully match the style (src/families/fidelity.ts LOW_FIDELITY). */
const LOW_FIDELITY = 0.7;
export interface FitExtras {
  unmodelledFeatures?: string[];
  /** 0–1, or a word ('high' | 'medium' | 'low'). */
  fidelity?: number | string | { score?: number; level?: string; note?: string; warning?: string; reasons?: string[] } | null;
  structureWarning?: string | { message?: string; note?: string } | null;
  evidence?: { frontQuad?: unknown; viewYawDeg?: number };
  viewYawDeg?: number;
}

/** The fit result with its optional extras, or null (manual, refused, legacy proposal). */
export const fitResult = computed<(FitResult & FitExtras) | null>(() => {
  const p = ui.analysis.proposal;
  if (!isFitResult(p) || ui.analysis.status === 'manual' || ui.analysis.status === 'refused') return null;
  return p as FitResult & FitExtras;
});

/** "2 doors, 1 drawer" — the features seen in the photo that the family does not build. */
export const unmodelled = computed<string[]>(() => {
  const list = fitResult.value?.unmodelledFeatures;
  return Array.isArray(list) ? list.filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim()) : [];
});

/** Low fidelity: the photo doesn't fully match the chosen style (with the family's own warning when given). */
export const lowFidelity = computed<{ note: string | null } | null>(() => {
  const r = fitResult.value;
  if (!r) return null;
  const f = r.fidelity;
  const w = r.structureWarning;
  const warnText = typeof w === 'string' ? w : w && typeof w === 'object' ? (w.message ?? w.note ?? null) : null;
  let low = false;
  let note: string | null = warnText?.trim() || null;
  if (typeof f === 'number') low = f < LOW_FIDELITY;
  else if (typeof f === 'string') low = /low|poor|partial/i.test(f);
  else if (f && typeof f === 'object') {
    low = (typeof f.score === 'number' && f.score < LOW_FIDELITY) || /low|poor|partial/i.test(f.level ?? '') || !!f.warning;
    // the family's reasons read best after our sentence; its own warning repeats it, so it is the fallback
    const reasons = Array.isArray(f.reasons) ? f.reasons.filter((x) => typeof x === 'string' && x.trim()) : [];
    note ??= reasons.length ? `It differs from your photo: ${reasons.slice(0, 3).join('; ')}` : f.warning ?? f.note ?? null;
  }
  if (warnText && warnText.trim()) low = true;
  return low ? { note } : null;
});

// ---------------------------------------------------------------------------------------------
// FND-16: the front face of the piece on the photo (TL, TR, BR, BL), normalised
// ---------------------------------------------------------------------------------------------
function toPt(v: unknown): Pt | null {
  if (Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number' && typeof v[1] === 'number') return { x: v[0], y: v[1] };
  if (v && typeof v === 'object' && typeof (v as Pt).x === 'number' && typeof (v as Pt).y === 'number') return { x: (v as Pt).x, y: (v as Pt).y };
  return null;
}
/** Accepts [[x,y]×4], [{x,y}×4] or {tl,tr,br,bl}; null unless all four corners are finite. */
export function quadOf(raw: unknown): [Pt, Pt, Pt, Pt] | null {
  let pts: (Pt | null)[] = [];
  if (Array.isArray(raw)) pts = raw.slice(0, 4).map(toPt);
  else if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    pts = [o.tl ?? o.topLeft, o.tr ?? o.topRight, o.br ?? o.bottomRight, o.bl ?? o.bottomLeft].map(toPt);
  }
  if (pts.length !== 4 || pts.some((p) => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;
  return pts as [Pt, Pt, Pt, Pt];
}
export const frontQuad = computed(() => quadOf(fitResult.value?.evidence?.frontQuad));
export const viewYaw = computed<number | null>(() => {
  const r = fitResult.value;
  const y = r?.evidence?.viewYawDeg ?? r?.viewYawDeg;
  return typeof y === 'number' && Number.isFinite(y) ? y : null;
});

// ---------------------------------------------------------------------------------------------
// FND-07: the closest kit material to the photo's finish (run-2 mapping table)
//   white, cream, painted or very pale → birch plywood, and say paint isn't modelled;
//   dark, walnut, mango or sheesham → sheesham;  mid or golden → oak;
//   light wood → oak when the photo names oak, else birch;  nothing said → no guess.
// ---------------------------------------------------------------------------------------------
export type Tone = 'painted' | 'dark' | 'mid' | 'pale';
// Black, charcoal and grey are paint or stain colours, not a timber: never preview them as sheesham (stress run 2b, p08).
const PAINTED = /\b(white|whitewash(?:ed)?|off-white|cream|ivory|painted|paint|lacquered white|very pale|melamine white|grey|gray|black|charcoal|ebonised|ebonized|anthracite)\b/i;
const DARK = /\b(dark|espresso|walnut|wenge|mahogany|sheesham|rosewood|chocolate|mango|teak)\b/i;
const OAK = /\boak\b/i;
const MID = /\b(mid|medium|golden|honey|amber|tan|brown|cherry|chestnut|acacia)\b/i;
const PALE = /\b(light|pale|natural|birch|maple|ash|pine|beech|blond|blonde|spruce|plywood|ply)\b/i;
/**
 * The tone of the photographed finish from the fit's colour and material words, or null when
 * they say nothing about it. Order matters: a painted or white finish first, then dark timbers
 * (walnut, mango, sheesham), then oak, then mid and golden tones, then light wood.
 */
export function toneOf(colour: string, material = ''): Tone | null {
  const c = colour ?? '';
  const all = `${c} ${material ?? ''}`;
  if (PAINTED.test(c) || (!c.trim() && /\b(white|painted|cream)\b/i.test(material ?? ''))) return 'painted';
  if (DARK.test(all)) return 'dark';
  if (OAK.test(all)) return 'mid';
  if (MID.test(c)) return 'mid';
  if (PALE.test(all)) return 'pale';
  return null;
}
const TONE_SPECIES: Record<Tone, string> = { painted: 'birch', dark: 'sheesham', mid: 'oak', pale: 'birch' };
/** The kit material Check previews the reconstruction in (appearance only), or null when the photo says nothing. */
export const checkMaterial = computed<{ stockId: string; label: string; tone: Tone } | null>(() => {
  const r = fitResult.value;
  if (!r) return null;
  const tone = toneOf(r.shared?.colour ?? '', r.shared?.material ?? '');
  if (!tone) return null;
  const sp = TONE_SPECIES[tone];
  const m = MATERIAL_CHOICES.find((c) => c.id.startsWith(sp) || c.stockId.startsWith(sp));
  return m ? { stockId: m.stockId, label: m.label, tone } : null;
});
